// ─────────────────────────────────────────────────────────────────────────────
// formatTileSummary — pure display helpers for Home's format tiles (#2319,
// M13.4). Two independent lines:
//
//   line 1 (formatTileSpec)   the round type's physical shape, e.g.
//                             "18 m · 40 cm · 10×3" — built from the SAME
//                             pieces the wizard's catalog detail line already
//                             uses (distanceSummary, lib/segments), never a
//                             new distance formatter.
//   line 2 (formatTileNotes)  the archer's remembered choices for this
//                             Format, e.g. "Recurve · Tue league" — bow style
//                             + first tag from the FormatStartDefaults row,
//                             when one exists. Renders "" (nothing) when the
//                             Format has never been started, or the archer's
//                             defaults carry neither.
// ─────────────────────────────────────────────────────────────────────────────
import { distanceSummary } from "./segments";
import { isCourseRoundType } from "./courseFormat";

function capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * The size range across a course round's segments, e.g. "35–65 cm" — ONLY
 * when the segments actually carry 2+ DISTINCT sizes (#2436 finding 1: a
 * custom NFAA field course whose targets vary 20–65 cm by distance). A
 * constant size (every catalog course row before this fix, and most custom
 * ones) renders nothing here — `formatTileSpec` already reads fine as just
 * the target count in that case, so this stays silent rather than adding
 * "35 cm" noise to every course tile.
 *
 * @param {Array|null} template  a round type's `segment_template`
 * @returns {string|null}
 */
function courseFaceSizeRange(template) {
  if (!Array.isArray(template)) return null;
  const sizes = [...new Set(template.map((s) => s?.face_size_cm).filter((v) => v != null))];
  if (sizes.length < 2) return null;
  return `${Math.min(...sizes)}–${Math.max(...sizes)} cm`;
}

/**
 * "18 m · 40 cm · 10×3" for a normal round; "{ends} targets" for a course
 * round (3D/field — no distance/face-size schedule to show), plus a size
 * range when the course's own targets vary (#2436 finding 1). Course-ness is
 * the shared `isCourseRoundType` predicate (#2400), so a `structure_type:
 * "targets"` row reads as targets here too.
 *
 * @param {object} rt  a catalog round type (RoundTypeOut)
 * @returns {string}
 */
export function formatTileSpec(rt) {
  if (!rt) return "";
  if (isCourseRoundType(rt)) {
    const label = rt.ends != null ? `${rt.ends} targets` : "";
    const sizeRange = courseFaceSizeRange(rt.segment_template);
    return sizeRange ? [label, sizeRange].filter(Boolean).join(" · ") : label;
  }
  const dist = distanceSummary(rt) || null;
  const template = Array.isArray(rt.segment_template) ? rt.segment_template : null;
  const faceSizeCm = template?.[0]?.face_size_cm ?? null;
  const size = faceSizeCm != null ? `${faceSizeCm} cm` : null;
  const struct = rt.ends && rt.arrows_per_end ? `${rt.ends}×${rt.arrows_per_end}` : null;
  return [dist, size, struct].filter(Boolean).join(" · ");
}

/**
 * "Recurve · Tue league" — bow style + first tag name from the archer's
 * remembered start-defaults for this Format. "" when there is no remembered
 * row, or it carries neither a bow style nor any tags.
 *
 * @param {object|null} defaults  a FormatStartDefaultsOut row, or null
 * @param {Map<number,string>} tagsById  tag id -> name
 * @returns {string}
 */
export function formatTileNotes(defaults, tagsById) {
  if (!defaults) return "";
  const parts = [];
  if (defaults.bow_style) parts.push(capitalize(defaults.bow_style));
  const firstTagId = defaults.tag_ids?.[0];
  const tagName = firstTagId != null ? tagsById?.get(firstTagId) : null;
  if (tagName) parts.push(tagName);
  return parts.join(" · ");
}
